import React, { useCallback, useEffect } from 'react';
import useTranslation from "~/app/hooks/useTranslation";
import { useStoreActions, useStoreState } from '~/store';
import { useHistory, useParams } from "react-router";
import { Machine, Project } from "~/types";
import { useAppHeader } from "~/app/modules/appHeader";
import { route } from "~/app/helpers/route";
import { MACHINES_DETAILS_ROUTES } from "~/machines/Details/Router";
import classes from "./style.module.scss";
import ListItem from "~/machines/Details/Registration/List/ListItem";
import moment from "moment";
import { PERMISSION_GROUPS } from "~/accessControl/permissionGroups";
import { PERIODIC_CHECK_DRAFT_STATUS } from '~/app/data/statuses/periodicCheck';
import { isPeriodicCheckAwaitingSync, usePendingPeriodicChecks } from '~/periodicCheck/usePendingPeriodicChecks';

const Equipments: React.FC = () => {
    const history = useHistory();
    const { t } = useTranslation("machine.details");
    const { t: tOffline } = useTranslation("offline");
    const { setData } = useAppHeader();
    const {projectId } = useParams<{ id: Machine['id'], projectId: Project['id'] }>();

    const machine = useStoreState(state => state.machine.data);
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const openFromMachine = useStoreActions(actions => actions.periodicCheck.openFromMachine);
    const pendingTargets = [
        { id: machine.id, inspectionType: machine.inspectionType },
        ...(machine.linkedMachines ?? []).map(linked => ({
            id: linked.id,
            inspectionType: linked.inspectionType,
        })),
    ];
    const { pending: pendingChecks, loaded: pendingChecksLoaded } = usePendingPeriodicChecks(
        pendingTargets,
        `${netWorkStatus}:${machine.lastDailyCheck?.pendingSync ?? ''}:${(machine.linkedMachines ?? []).map(linked => linked.lastDailyCheck?.pendingSync ?? '').join(',')}`,
    );

    useEffect(() => {
        setData({
            title: t('periodicCheck'),
            subTitle: (machine.intern + " - " + machine.name).toUpperCase(),
            backLink: route(MACHINES_DETAILS_ROUTES.details, { id: machine.id, projectId }),
            showUser: false,
            machineId: '',
            projectId: '',
            showMachineMessages: false,
            isIssueDetailPage: false,
            isServiceDetailPage: false,
            notification: false,
            search: false
        });
    }, [setData, machine, t, projectId]);

    const showChecks = useCallback(async (equipment: Machine) => {
        await openFromMachine({
            equipment,
            projectId,
            inspectionType: equipment.inspectionType,
            lastRegisteredCheck: equipment.lastDailyCheck,
        });
        history.push(route(MACHINES_DETAILS_ROUTES.periodicCheck, { id: machine.id, projectId }))
    }, [history, openFromMachine, machine.id, projectId]);

    const getEquipmentTitles = (machineItem: Machine, isMain: boolean = false) => {
        const checkPoints = (machineItem.checks || []).length;
        const lastCheckRaw = machineItem.lastDailyCheck?.date;
        const lastDateFormatted = lastCheckRaw ? moment(lastCheckRaw).format('D. MMM YYYY') : null;
        const checkpointsText = `${checkPoints} ${t('checkpoints')}`;
        const lastRegisteredText = lastDateFormatted ? ` - ${t('last')}: ${lastDateFormatted}` : '';
        const namePrefix = isMain ? '' : `${machineItem.name.split(' ')[0]} - `;

        const subTitle = {
            name: `${namePrefix}${checkpointsText}${lastRegisteredText}`,
            checked: false
        };

        const awaitingSync = isPeriodicCheckAwaitingSync(
            pendingChecks,
            pendingChecksLoaded,
            machineItem.id,
            machineItem.inspectionType,
            machineItem.lastDailyCheck?.pendingSync,
        );
        const lastIsToday = !!lastCheckRaw && moment(lastCheckRaw).isSame(moment(), 'day');
        const thirdTitle = awaitingSync
            ? {
                name: tOffline('awaitingSynchronization'),
                checked: true,
            }
            : lastCheckRaw
            ? {
                name: machineItem.lastDailyCheck?.status === PERIODIC_CHECK_DRAFT_STATUS
                    ? t('periodicCheckDraft', { date: moment(lastCheckRaw) })
                    : lastIsToday
                        ? t('periodicCheckToday')
                        : t('periodicCheckPast', {date: moment(lastCheckRaw)}),
                checked: lastIsToday && machineItem.lastDailyCheck?.status !== PERIODIC_CHECK_DRAFT_STATUS,
                ...(machineItem.lastDailyCheck?.status === PERIODIC_CHECK_DRAFT_STATUS || !lastIsToday
                    ? { className: ['periodic-check-subtitle', 'pending'] }
                    : {})
            }
            : undefined;

        return {subTitle, thirdTitle};
    };

    return (
        <div className={classes['equipments-page']}>
            <div className={classes['page-title-container']}>
                <div className={classes['page-title']}>{t('selectEquipment')}</div>
                <div className={classes['page-subtitle']}>{t('selectEquipmentDescription')}</div>
            </div>

            <div className={classes['section-title']}>{t('mainUnit')}</div>
            <ListItem
                item={{
                    icon: 'local_shipping',
                    title: machine.name,
                    ...getEquipmentTitles(machine, true),
                    rightIcon: 'keyboard_arrow_right',
                    onSelect: () => showChecks(machine),
                    requiredPermissions: PERMISSION_GROUPS.readWritePeriodicCheck
                }}
            />

            {machine.linkedMachines && machine.linkedMachines.length > 0 && (
                <>
                    <div className={classes['section-title']}>
                        {t('additionalEquipment')} ({machine.linkedMachines.length})
                    </div>
                    {machine.linkedMachines.map((linked, index) => (
                        <ListItem
                            key={index}
                            item={{
                                icon: 'build',
                                title: linked.name,
                                ...getEquipmentTitles(linked),
                                rightIcon: 'keyboard_arrow_right',
                                onSelect: () => showChecks(linked),
                                requiredPermissions: PERMISSION_GROUPS.readWritePeriodicCheck
                            }}
                        />
                    ))}
                </>
            )}
        </div>
    );
};

export default Equipments;
